import { useSprings } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import { useRef, useState } from "react";
import { useMeasure, usePrevious } from "react-use";
import { map, rangeFromZero } from "./generators";

export const useScrollableList = <T extends Element>(
  count: number,
  page: number,
  onPageChange?: (page: number) => void
) => {
  const [component, { width }] = useMeasure<T>();
  const ref = useRef<T | null>(null);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const prevWidth = usePrevious(width);
  const [props] = useSprings(
    count,
    [
      ...map(rangeFromZero(count), (i) => ({
        to:
          width === 0
            ? { display: "none" }
            : { x: (i - page) * width + offset, display: "" },
        immediate: dragging || width !== prevWidth,
      })),
    ],
    [width, page, offset]
  );

  useDrag(
    (e) => {
      if (!onPageChange) return;
      if (dragging !== e.down) setDragging(e.down);
      if (e.down) {
        const [xOffset] = e.movement;
        setOffset(xOffset);
      } else {
        if (offset * 2 < -width) onPageChange(Math.min(count - 1, page + 1));
        else if (offset * 2 > width) onPageChange(Math.max(0, page - 1));
        setOffset(0);
      }
    },
    {
      delay: true,
      enabled: !!onPageChange,
      target: ref,
      preventDefault: true,
      eventOptions: { capture: true, passive: false },
    }
  );

  return [
    (e: T) => {
      component(e);
      ref.current = e;
    },
    props,
  ] as const;
};
